import { isThemePreference, ThemePreference } from '../../common/types/theme.js'
import { View } from '../types.js'
import { portalPut } from '../views/portal/utils/api.js'
import { animateThemeChange, ThemeChangeOrigin } from './theme-transition.js'

/**
 * Teema DOM-leping: `<html data-theme="light|dark">`; `system` = atribuut puudub
 * ja CSS järgib `prefers-color-scheme`. Sama loogika jookseb inline-skriptina
 * index.html-is enne CSS-i, et esimene render ei vilguks — hoia need sünkroonis.
 */
export const THEME_STORAGE_KEY = 'portal-theme'

/** Brauseri chrome'i värv (meta theme-color) kummaski teemas. */
const THEME_COLOR = { light: '#362360', dark: '#0b0813' } as const

export const resolveTheme = (pref: ThemePreference, osPrefersDark: boolean): 'light' | 'dark' =>
    pref === 'system' ? (osPrefersDark ? 'dark' : 'light') : pref

/** Viimati lokaalselt salvestatud eelistus; `system`, kui puudub või vigane. */
export const loadInitialTheme = (): ThemePreference => {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY)
        return isThemePreference(stored) ? stored : 'system'
    } catch {
        return 'system'
    }
}

export const persistThemeLocally = (pref: ThemePreference): void => {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, pref)
    } catch {
        // privaatne aken / kvoot: valik kehtib selle sessiooni, ei ela reloadi üle
    }
}

const osPrefersDark = (): boolean =>
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches

/** Rakendab eelistuse DOM-ile. `system` eemaldab atribuudi ja CSS järgib OS-i. */
export const applyTheme = (pref: ThemePreference): void => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (pref === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', pref)
    const meta = document.querySelector('meta[name="theme-color"]') as { content: string } | null
    if (meta) meta.content = THEME_COLOR[resolveTheme(pref, osPrefersDark())]
}

/** Teemavaliku käsitleja: `origin` on lüliti nupu keskpunkt (ring-laine algus). */
export type ThemeSelectHandler = (pref: ThemePreference, origin?: ThemeChangeOrigin) => void

/**
 * Kasutaja valik: state + DOM + localStorage + server. PUT-i viga ei tühista
 * valikut selles sessioonis; järgmisel laadimisel võidab serveri väärtus
 * (syncThemeFromServer), seega salvestamata valik ei jää püsima.
 */
export const setTheme = (view: View, pref: ThemePreference, origin?: ThemeChangeOrigin): void => {
    view.state.themePreference = pref
    // Ring-laine lüliti nupust (origin); ilma originita vahetub kohe.
    animateThemeChange(() => applyTheme(pref), origin)
    persistThemeLocally(pref)
    void portalPut('/api/portal/me/appearance', { themePreference: pref }).then((res) => {
        if (!res.ok) console.warn('Theme preference not saved:', res.error)
    })
    view.update()
}

/** Pärast /api/portal/me laadimist: server on tõeallikas üle seadmete. */
export const syncThemeFromServer = (view: View, serverPref: ThemePreference): void => {
    if (view.state.themePreference === serverPref) return
    view.state.themePreference = serverPref
    applyTheme(serverPref)
    persistThemeLocally(serverPref)
    view.update()
}
